class AdvancedSlider {
  constructor(slider, options = {}) {
    this.slider = slider;
    this.track = slider.querySelector('.slider-track');
    this.slides = Array.from(this.track.children);
    this.currentIndex = 0;

    this.settings = {
      arrows: slider.dataset.arrows === 'true',
      dots: slider.dataset.dots === 'true',
      draggable: slider.dataset.draggable === 'true',
      loop: slider.dataset.loop === 'true',
      autoplay: slider.dataset.autoplay === 'true',
      autoplayDelay: parseInt(slider.dataset.autoplaydelay || '4000'),
      gap: parseInt(slider.dataset.gap || '15'),
      slidesDesktop: parseFloat(slider.dataset.slidesDesktop || '3'),
      slidesTablet: parseFloat(slider.dataset.slidesTablet || '2'),
      slidesMobile: parseFloat(slider.dataset.slidesMobile || '1'),
      scrollDesktop: parseFloat(slider.dataset.scrollDesktop || '1'),
      scrollTablet: parseFloat(slider.dataset.scrollTablet || '1'),
      scrollMobile: parseFloat(slider.dataset.scrollMobile || '1'),
      autoHeight: slider.dataset.autoheight === 'true' // 👈 new option
    };

    this.touch = { startX: 0, active: false };
    this.autoplayTimer = null;

    this.setup(options.containerWidth);
  }

  // `containerWidth` is the slider's clientWidth measured before any slider on the page
  // wrote to the DOM (see the DOMContentLoaded handler below). Without it each slider's
  // first clientWidth read forces a layout after the previous slider's writes.
  setup(containerWidth) {
    this.setResponsive();
    this.createControls();
    this.attachEvents();
    this.update(true, containerWidth);
    if (this.settings.autoplay) this.startAutoplay();

    // 👇 Initial height adjust
    if (this.settings.autoHeight) this.adjustHeight();
  }

  setResponsive() {
    const width = window.innerWidth;
    if (width < 768) {
      this.slidesToShow = this.settings.slidesMobile;
      this.slidesToScroll = this.settings.scrollMobile;
      this.currentGap = 5;
    } else if (width < 992) {
      this.slidesToShow = this.settings.slidesTablet;
      this.slidesToScroll = this.settings.scrollTablet;
      this.currentGap = this.settings.gap;
    } else {
      this.slidesToShow = this.settings.slidesDesktop;
      this.slidesToScroll = this.settings.scrollDesktop;
      this.currentGap = this.settings.gap;
    }

    if (this.settings.dots) {
      this.rebuildDots();
    }
  }

  createControls() {
    if (this.slides.length <= this.slidesToShow) return;

    if (this.settings.arrows) {
      const prev = document.createElement('button');
      const next = document.createElement('button');
      prev.className = 'slider-arrow prev';
      next.className = 'slider-arrow next';
      prev.setAttribute('aria-label', 'Previous slide');
      next.setAttribute('aria-label', 'Next slide');
      prev.innerHTML = `<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" width="40" height="40"><path fill="transparent" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M22.5 12.5 15 20l7.5 7.5"/></svg>`;
      next.innerHTML = `<svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" width="40" height="40"><path fill="transparent" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.5 12.5 25 20l-7.5 7.5"/></svg>`;
      prev.addEventListener('click', () => this.move(-1));
      next.addEventListener('click', () => this.move(1));
      this.slider.append(prev, next);
    }

    if (this.settings.dots) {
      this.dotsContainer = document.createElement('div');
      this.dotsContainer.className = 'slider-dots';
      this.slider.appendChild(this.dotsContainer);
      this.rebuildDots();
      this.dotsContainer.querySelectorAll('button.dot').forEach((dot, index) => {
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      });
    }


    
  }

  rebuildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    const total = Math.ceil((this.slides.length - this.slidesToShow) / this.slidesToScroll) + 1;
    for (let i = 0; i < total; i++) {
      const dot = document.createElement('button');
      dot.className = 'dot';
      // Labelled here because resize rebuilds the dots and drops any label set earlier.
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => {
        this.currentIndex = i * this.slidesToScroll;
        this.update();
        this.restartAutoplay();
      });
      this.dotsContainer.appendChild(dot);
    }
    this.updateDots();
  }

  attachEvents() {
    window.addEventListener('resize', () => {
      this.setResponsive();
      this.update(true);
      if (this.settings.autoHeight) this.adjustHeight();
    });

    if (this.settings.draggable) {
      this.track.addEventListener('mousedown', e => this.dragStart(e));
      this.track.addEventListener('touchstart', e => this.dragStart(e));
      this.track.addEventListener('mousemove', e => this.dragMove(e));
      this.track.addEventListener('touchmove', e => this.dragMove(e));
      this.track.addEventListener('mouseup', () => this.dragEnd());
      this.track.addEventListener('touchend', () => this.dragEnd());
      this.track.addEventListener('mouseleave', () => this.dragEnd());
    }
  }

  update(skipTransition = false, measuredWidth) {
    const gap = this.currentGap;
    const visibleSlides = this.slidesToShow;
    const totalSlides = this.slides.length;

    const containerWidth = measuredWidth ?? this.slider.clientWidth;
    const slideWidthPx = (containerWidth - (gap * (visibleSlides - 1))) / visibleSlides;

    this.slides.forEach(slide => {
      slide.style.flex = `0 0 ${slideWidthPx}px`;
      slide.style.width = `${slideWidthPx}px`;
    });

    // currentIndex counts slides, so the cap is in slides too — dividing by
    // slidesToScroll here stopped a 3-at-a-time slider one slide past the start.
    const maxIndex = Math.max(0, Math.ceil(totalSlides - visibleSlides));
    if (!this.settings.loop) {
      this.currentIndex = Math.max(0, Math.min(this.currentIndex, maxIndex));
    } else {
      if (this.currentIndex > maxIndex) this.currentIndex = 0;
      if (this.currentIndex < 0) this.currentIndex = maxIndex;
    }

    const translateX = (slideWidthPx + gap) * this.currentIndex * -1;

    this.track.style.transition = skipTransition ? 'none' : 'transform 0.5s ease';
    this.track.style.transform = `translateX(${translateX}px)`;
    this.track.style.gap = `${gap}px`;

    this.updateDots();

    // 👇 Auto height adjust after each update
    if (this.settings.autoHeight) this.adjustHeight();
  }

  adjustHeight() {
    const visibleSlides = this.slides.slice(
      this.currentIndex,
      this.currentIndex + this.slidesToShow
    );

    let maxHeight = 0;

    visibleSlides.forEach(slide => {
      const img = slide.querySelector('img');
      if (img) {
        if (img.complete) {
          maxHeight = Math.max(maxHeight, img.naturalHeight * (slide.offsetWidth / img.naturalWidth));
        } else {
          img.addEventListener('load', () => this.adjustHeight(), { once: true });
        }
      } else {
        maxHeight = Math.max(maxHeight, slide.offsetHeight);
      }
    });

    if (maxHeight > 0) {
      this.slider.style.height = `${maxHeight}px`;
      this.track.style.height = `${maxHeight}px`;
    }
  }

  updateDots() {
    if (!this.dotsContainer) return;
    const dots = Array.from(this.dotsContainer.children);
    dots.forEach(dot => dot.classList.remove('active'));
    const activeDot = Math.round(this.currentIndex / this.slidesToScroll);
    if (dots[activeDot]) dots[activeDot].classList.add('active');
  }

  move(direction) {
    this.currentIndex += direction * this.slidesToScroll;
    this.update();
    this.restartAutoplay();
  }

  dragStart(e) {
    this.touch.active = true;
    this.touch.startX = e.touches ? e.touches[0].pageX : e.pageX;
  }

  dragMove(e) {
    if (!this.touch.active) return;
    const currentX = e.touches ? e.touches[0].pageX : e.pageX;
    const dist = this.touch.startX - currentX;
    if (Math.abs(dist) > 50) {
      this.move(dist > 0 ? 1 : -1);
      this.touch.active = false;
    }
  }

  dragEnd() {
    this.touch.active = false;
  }

  startAutoplay() {
    this.autoplayTimer = setInterval(() => this.move(1), this.settings.autoplayDelay);
  }

  restartAutoplay() {
    if (!this.settings.autoplay) return;
    clearInterval(this.autoplayTimer);
    this.startAutoplay();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const sliders = Array.from(document.querySelectorAll('.custom-slider'));
  // Read every width first, then build. Interleaving a read with the previous
  // slider's DOM writes forced one layout per slider during page load.
  const widths = sliders.map(slider => slider.clientWidth);
  sliders.forEach((slider, index) => new AdvancedSlider(slider, { containerWidth: widths[index] }));
});